import { TextFontFamily, TextAlignment, TextTransform, TextColor } from './constants.js';
import { TextOverflow } from './types.js';
import { getTextStyleValues, getThemeTextStyleValues } from './utils.js';
import React from 'react';
import { TextElement } from './styles.js';
import '../../themes/default/borderRadius/index.js';
import '../../themes/default/borderWidth/index.js';
import '../../themes/default/color/index.js';
import '../../themes/default/shape/index.js';
import '../../themes/default/size/index.js';
import '../../themes/default/spacing/index.js';
import '../../theming/themes/caviar.js';
import '../../theming/themes/merchant.js';
import '../../theming/themes/merchant-next.js';
import '../../theming/themes/internal-tools.js';
import { ThemingContext } from '../../theming/theming.js';
import { TypographyDensities } from '../../theming/typography/constants.js';
import 'lodash.clonedeep';
import 'lodash.merge';
import get from 'lodash.get';

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
var DEFAULT_PROPS = {
  color: 'TextPrimary',
  family: TextFontFamily.SansSerif,
  overflow: TextOverflow.Wrap,
  textAlign: TextAlignment.Left,
  textTransform: TextTransform.Normal
};

var StyledBaseText = function StyledBaseText(_ref) {
  var _ref$color = _ref.color,
      color = _ref$color === void 0 ? DEFAULT_PROPS.color : _ref$color,
      _ref$family = _ref.family,
      family = _ref$family === void 0 ? DEFAULT_PROPS.family : _ref$family,
      _ref$overflow = _ref.overflow,
      overflow = _ref$overflow === void 0 ? DEFAULT_PROPS.overflow : _ref$overflow,
      _ref$textAlign = _ref.textAlign,
      textAlign = _ref$textAlign === void 0 ? DEFAULT_PROPS.textAlign : _ref$textAlign,
      _ref$textTransform = _ref.textTransform,
      textTransform = _ref$textTransform === void 0 ? DEFAULT_PROPS.textTransform : _ref$textTransform,
      styles = _ref.styles,
      mobileStyles = _ref.mobileStyles,
      tabletStyles = _ref.tabletStyles,
      desktopStyles = _ref.desktopStyles,
      wideScreenStyles = _ref.wideScreenStyles,
      tag = _ref.tag,
      props = _objectWithoutProperties(_ref, ["color", "family", "overflow", "textAlign", "textTransform", "styles", "mobileStyles", "tabletStyles", "desktopStyles", "wideScreenStyles", "tag"]);

  var theme = React.useContext(ThemingContext);
  var themeDensityString = "".concat(get(theme, "Typography.Density", TypographyDensities.Regular), "Density");
  var completeSemanticDefinition = get(theme, "Typography.TextStyles.".concat(styles)); // Gets the `MobileWidth` and `DesktopWidth` values from
  // the theme's TextStyle definition for the given semantic

  var textStyleValues = getTextStyleValues({
    allValues: completeSemanticDefinition,
    density: themeDensityString
  }); // Returns an object of properties for use in `TextElement`
  // styled-component logic

  var stylesValues = getThemeTextStyleValues({
    completeSemanticDefinition: completeSemanticDefinition,
    themeDensityString: themeDensityString,
    defaults: DEFAULT_PROPS
  });

  if (typeof mobileStyles === 'string') {
    mobileStyles = getThemeTextStyleValues({
      completeSemanticDefinition: theme.Typography.TextStyles[mobileStyles],
      themeDensityString: themeDensityString,
      defaults: DEFAULT_PROPS
    });
  }

  if (typeof tabletStyles === 'string') {
    tabletStyles = getThemeTextStyleValues({
      completeSemanticDefinition: theme.Typography.TextStyles[tabletStyles],
      themeDensityString: themeDensityString,
      defaults: DEFAULT_PROPS,
      includeDesktopOverrides: true
    });
  }

  if (typeof desktopStyles === 'string') {
    desktopStyles = getThemeTextStyleValues({
      completeSemanticDefinition: theme.Typography.TextStyles[desktopStyles],
      themeDensityString: themeDensityString,
      defaults: DEFAULT_PROPS,
      includeDesktopOverrides: true
    });
  }

  if (typeof wideScreenStyles === 'string') {
    wideScreenStyles = getThemeTextStyleValues({
      completeSemanticDefinition: theme.Typography.TextStyles[wideScreenStyles],
      themeDensityString: themeDensityString,
      defaults: DEFAULT_PROPS,
      includeDesktopOverrides: true
    });
  }

  return /*#__PURE__*/React.createElement(TextElement, _extends({
    as: tag,
    textColor: color && (theme.Colors[color] || TextColor[color]),
    themeFontFamily: theme.Typography.FontFamily,
    family: family,
    overflow: overflow,
    textAlign: textAlign,
    textTransform: textTransform,
    styles: stylesValues,
    themeDesktopOverrideValues: _objectSpread({}, textStyleValues.desktopValues),
    mobileStyles: mobileStyles,
    tabletStyles: tabletStyles,
    desktopStyles: desktopStyles,
    wideScreenStyles: wideScreenStyles,
    themeDecorationColor: props.textDecorationColor && (theme.Colors[props.textDecorationColor] || TextColor[props.textDecorationColor])
  }, props));
};

export { StyledBaseText as default };
